// V2 Tempo patch — per-match Mission Banner. A small top-of-screen badge
// that gives the player a concrete sub-goal beyond "win the match." Picks
// one of five randomised missions at match start, tracks progress from
// snapshots, and on completion awards bonus XP + plays a small celebration
// pulse before fading itself out.
//
// Why: CrazyGamesTricks.md §5.2 — "micro-goals every 20-40 seconds." The
// match goal of 'destroy enemy core' is too far-away for a new player to
// chase; a mission like 'capture 3 relays' makes every relay flip a tick
// toward a visible reward, independent of whether the match is being won
// or lost.
//
// Pure presentation (Rule 1). DOM-based; sits at the top of the screen,
// below the timer/HUD and above the gameplay canvas. Reads MatchState
// snapshots directly. Awards 25 bonus XP on completion via the same Xp
// module used by end-of-match summaries; the cloud sync layer carries it
// across devices for free.

import type { MatchState, PlayerId } from '@signalclash/shared';

import { applyMissionXpModifier } from '../save/WeeklyModifier.js';

const ROOT_ID = 'mission-banner';
const STYLE_ID = 'mission-banner-styles';
const XP_KEY = 'signalclash.profile.xp';
const MISSION_XP_BASE_REWARD = 25;

interface MissionDef {
  id: string;
  label: string;
  // Computes current progress from the latest snapshot. Returns the
  // numerator only; goal is the denominator and is fixed at pick time.
  progress: (snap: MatchState, viewerId: PlayerId, baseline: MissionBaseline) => number;
  goal: number;
}

// V2 Tempo — per-match baseline so missions that count "since start" don't
// reward stats inherited from a previous match's residual state.
interface MissionBaseline {
  startTick: number;
  initialSquadsBuilt: number;
  initialEnemyCoreHp: number;
}

// Five missions. All readable from the public snapshot — no sim changes
// needed. Goals tuned so a typical 6-minute match has each mission's
// completion arriving comfortably before the climax.
const MISSIONS: readonly MissionDef[] = [
  {
    id: 'capture_relays',
    label: 'Capture 3 relays',
    goal: 3,
    progress: (snap, viewerId) => {
      let n = 0;
      for (const node of Object.values(snap.controlNodes)) {
        if (node.ownerId === viewerId) n += 1;
      }
      return n;
    },
  },
  {
    id: 'build_squads',
    label: 'Build 4 squads',
    goal: 4,
    progress: (snap, viewerId, baseline) => {
      const built = snap.stats.squadsBuilt[viewerId] ?? 0;
      return Math.max(0, built - baseline.initialSquadsBuilt);
    },
  },
  {
    id: 'destroy_enemy_squads',
    label: 'Destroy 5 enemy squads',
    goal: 5,
    progress: (snap, viewerId) => {
      // Snapshot doesn't expose kill counts, so we infer "enemy squad count
      // decay since baseline" — close enough for a player-facing goal. The
      // viewer's own kill tally on the EOM card is authoritative; this is
      // just a directional progress bar.
      const enemyId: PlayerId = viewerId === 0 ? 1 : 0;
      // EnemyCore is owned by enemyId; if enemy has fewer squads alive than
      // they've built, the difference includes squads we (and turrets)
      // killed. Use `squadsBuilt - squadCount` for the enemy player.
      const enemyBuilt = snap.stats.squadsBuilt[enemyId] ?? 0;
      const enemyAlive = snap.players[enemyId]?.squadCount ?? 0;
      return Math.max(0, enemyBuilt - enemyAlive);
    },
  },
  {
    id: 'damage_enemy_core',
    label: 'Deal 500 damage to enemy Core',
    goal: 500,
    progress: (snap, viewerId, baseline) => {
      const enemyId: PlayerId = viewerId === 0 ? 1 : 0;
      let enemyCoreHp = 0;
      for (const core of Object.values(snap.cores)) {
        if (core.ownerId === enemyId) {
          enemyCoreHp = core.hp;
          break;
        }
      }
      return Math.max(0, baseline.initialEnemyCoreHp - enemyCoreHp);
    },
  },
  {
    id: 'accumulate_signal',
    label: 'Stockpile 600 signal',
    goal: 600,
    progress: (snap, viewerId) => {
      return Math.max(0, Math.floor(snap.players[viewerId]?.signal ?? 0));
    },
  },
] as const;

export interface MissionBannerOptions {
  // Picks a mission deterministically per match. Defaults to Math.random.
  pickIndex?: (length: number) => number;
}

export class MissionBanner {
  private root: HTMLDivElement;
  private labelEl: HTMLSpanElement;
  private progressEl: HTMLSpanElement;
  private barEl: HTMLDivElement;
  private rewardEl: HTMLSpanElement;
  private mission: MissionDef | null = null;
  private baseline: MissionBaseline | null = null;
  private viewerId: PlayerId = 0;
  private completed = false;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private pickIndex: (length: number) => number;

  constructor(opts: MissionBannerOptions = {}) {
    this.pickIndex = opts.pickIndex ?? ((n) => Math.floor(Math.random() * n));
    injectStyles();
    this.root = ensureRoot();
    this.root.innerHTML = '';
    this.root.style.display = 'none';
    this.root.classList.remove('mission-banner--complete');

    const wrapper = document.createElement('div');
    wrapper.className = 'mission-banner__inner';

    const titleRow = document.createElement('div');
    titleRow.className = 'mission-banner__title-row';
    const chip = document.createElement('span');
    chip.className = 'mission-banner__chip';
    chip.textContent = 'MISSION';
    this.labelEl = document.createElement('span');
    this.labelEl.className = 'mission-banner__label';
    this.rewardEl = document.createElement('span');
    this.rewardEl.className = 'mission-banner__reward';
    this.rewardEl.textContent = `+${applyMissionXpModifier(MISSION_XP_BASE_REWARD)} XP`;
    titleRow.append(chip, this.labelEl, this.rewardEl);

    const progressRow = document.createElement('div');
    progressRow.className = 'mission-banner__progress-row';
    this.barEl = document.createElement('div');
    this.barEl.className = 'mission-banner__bar';
    const barFill = document.createElement('div');
    barFill.className = 'mission-banner__bar-fill';
    this.barEl.appendChild(barFill);
    this.progressEl = document.createElement('span');
    this.progressEl.className = 'mission-banner__progress';
    progressRow.append(this.barEl, this.progressEl);

    wrapper.append(titleRow, progressRow);
    this.root.appendChild(wrapper);
  }

  // Start a new mission for the upcoming match. `viewerId` is the local
  // player's slot. The first observed snapshot establishes the baselines
  // (squadsBuilt/initialEnemyCoreHp) so completed missions don't carry
  // residue from a previous match.
  start(viewerId: PlayerId): void {
    this.viewerId = viewerId;
    this.completed = false;
    this.baseline = null;
    this.mission = MISSIONS[this.pickIndex(MISSIONS.length)] ?? MISSIONS[0]!;
    this.labelEl.textContent = this.mission.label;
    this.rewardEl.textContent = `+${applyMissionXpModifier(MISSION_XP_BASE_REWARD)} XP`;
    this.progressEl.textContent = `0 / ${this.mission.goal}`;
    this.setFill(0);
    this.root.classList.remove('mission-banner--complete');
    this.root.style.display = 'flex';
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  // Read a fresh snapshot. The first call after start() establishes the
  // per-match baseline; subsequent calls just update the bar.
  observeSnapshot(snap: MatchState): void {
    if (this.mission === null || this.completed) return;
    if (this.baseline === null) {
      const enemyId: PlayerId = this.viewerId === 0 ? 1 : 0;
      let enemyCoreHp = 1500;
      for (const core of Object.values(snap.cores)) {
        if (core.ownerId === enemyId) {
          enemyCoreHp = core.maxHp;
          break;
        }
      }
      this.baseline = {
        startTick: snap.tick,
        initialSquadsBuilt: snap.stats.squadsBuilt[this.viewerId] ?? 0,
        initialEnemyCoreHp: enemyCoreHp,
      };
    }
    const current = Math.min(this.mission.goal, Math.floor(
      this.mission.progress(snap, this.viewerId, this.baseline),
    ));
    this.progressEl.textContent = `${current} / ${this.mission.goal}`;
    this.setFill(current / this.mission.goal);
    if (current >= this.mission.goal) {
      this.markComplete();
    }
  }

  // Called by ClientApp when the match ends. Hides the banner cleanly
  // regardless of completion state — the celebration animation handles
  // the "you completed it" case before this lands.
  onMatchEnded(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.root.style.display = 'none';
    this.root.classList.remove('mission-banner--complete');
    this.mission = null;
    this.baseline = null;
    this.completed = false;
  }

  dispose(): void {
    if (this.hideTimer !== null) clearTimeout(this.hideTimer);
    this.root.remove();
  }

  private markComplete(): void {
    if (this.completed) return;
    this.completed = true;
    this.root.classList.add('mission-banner--complete');
    awardMissionXp(applyMissionXpModifier(MISSION_XP_BASE_REWARD));
    // Linger long enough for the player to notice the pop, then fade out.
    // The pulse animation is CSS-driven so we don't need rAF here.
    this.hideTimer = setTimeout(() => {
      this.root.style.display = 'none';
      this.hideTimer = null;
    }, 3500);
  }

  private setFill(ratio01: number): void {
    const r = Math.max(0, Math.min(1, ratio01));
    const fill = this.barEl.querySelector('.mission-banner__bar-fill') as HTMLDivElement | null;
    if (fill !== null) fill.style.width = `${(r * 100).toFixed(1)}%`;
  }
}

function awardMissionXp(amount: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(XP_KEY);
    const prev = raw === null ? 0 : Math.max(0, Number.parseInt(raw, 10) || 0);
    localStorage.setItem(XP_KEY, String(prev + amount));
  } catch {
    // Quota / privacy mode — non-fatal.
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
/* V2 Tempo polish: parked directly below the resources panel as the
 * SECOND tile of the top-left "Command Console" stack. Border / gradient /
 * clip-path / cyan rim match .hud__resources so the two panels read as
 * one column instead of two random badges. top: 116 clears the resources
 * panel even at its tallest (3 stat rows + 19px primary number).
 *
 * Width matches the resources panel's min-width (190px) so the right
 * edges align; longer mission labels wrap to two lines rather than
 * pushing the badge wider than the stack above it. */
#${ROOT_ID} {
  position: fixed;
  top: 116px;
  left: 12px;
  display: none;
  z-index: 12;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  pointer-events: none;
}
.mission-banner__inner {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 16px;
  background: linear-gradient(180deg, rgba(20,28,38,0.85), rgba(10,14,20,0.9));
  border: 1px solid #2c3a48;
  border-radius: 0;
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  box-shadow: 0 0 0 1px rgba(106,216,255,0.06), 0 6px 20px rgba(0,0,0,0.5), inset 0 0 18px rgba(58,123,213,0.06);
  min-width: 190px;
  max-width: 220px;
  transition: border-color 0.4s ease, box-shadow 0.4s ease;
}
/* Header row: MISSION chip (cyan, matches .hud-stat__label tint) +
 * label that's allowed to wrap (the labels run 16-30 chars and the
 * panel is narrow) + tiny amber XP reward. The chip / label / reward
 * vocabulary mirrors .hud-stat: cyan = field, off-white = value, amber
 * = primary number. */
.mission-banner__title-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 11px;
  line-height: 1.25;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #cfd6dd;
}
.mission-banner__chip {
  flex-shrink: 0;
  color: #6ad8ff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  opacity: 0.85;
  padding-top: 1px;
}
.mission-banner__label {
  flex: 1;
  color: #eef3f8;
  white-space: normal;
  word-spacing: 0.05em;
}
.mission-banner__reward {
  flex-shrink: 0;
  color: #ffe680;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-shadow: 0 0 8px rgba(255,230,128,0.45);
  padding-top: 1px;
}
.mission-banner__progress-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mission-banner__bar {
  flex: 1;
  height: 5px;
  background: rgba(5, 9, 17, 0.85);
  border: 1px solid rgba(106, 216, 255, 0.18);
  border-radius: 1px;
  overflow: hidden;
}
.mission-banner__bar-fill {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #1ea7ff, #6fdcff);
  box-shadow: 0 0 8px rgba(30,167,255,0.40);
  transition: width 0.3s ease;
}
.mission-banner__progress {
  color: #6ad8ff;
  font-size: 10px;
  letter-spacing: 0.06em;
  min-width: 44px;
  text-align: right;
  font-weight: 700;
}
/* Completion celebration — gold border + pulse + brighter bar. */
#${ROOT_ID}.mission-banner--complete .mission-banner__inner {
  border-color: #f5e76e;
  box-shadow: 0 0 24px rgba(245,231,110,0.55);
  animation: mission-banner-complete 0.6s ease-out;
}
#${ROOT_ID}.mission-banner--complete .mission-banner__bar-fill {
  background: linear-gradient(90deg, #f5e76e, #ffd86e);
}
#${ROOT_ID}.mission-banner--complete .mission-banner__reward {
  animation: mission-reward-pop 0.6s ease-out;
}
@keyframes mission-banner-complete {
  0% { transform: scale(1); }
  40% { transform: scale(1.06); }
  100% { transform: scale(1); }
}
@keyframes mission-reward-pop {
  0% { transform: scale(1); color: #f5e76e; }
  50% { transform: scale(1.4); color: #ffffff; text-shadow: 0 0 12px #f5e76e; }
  100% { transform: scale(1); color: #f5e76e; }
}
@media (max-width: 640px) {
  #${ROOT_ID} { top: 100px; }
  .mission-banner__inner { min-width: 170px; max-width: 190px; padding: 8px 12px; }
  .mission-banner__title-row { font-size: 10px; }
}
`;
  document.head.appendChild(style);
}
